import _ from 'lodash';

import { parseCalendarDate } from 'utils/date';
import { checkIsImpoundCollected } from 'utils/impounds';

import { toWorksheetDateValue } from '../../components/WorksheetFieldsLayout/components/WorksheetDateField/WorksheetDateField.utils';
import {
  WORKSHEET_FEE_PREFILL_NAMES_SEPARATOR,
  WORKSHEET_FEE_PREFILL_SOURCE_PREFIX,
  WORKSHEET_PREFILL_SOURCE,
} from '../../components/WorksheetFieldsLayout/WorksheetFieldsLayout.constants';
import { parseWorksheetFieldSettings } from '../../components/WorksheetFieldsLayout/WorksheetFieldsLayout.utils';
import {
  getProposedPropertyExpenses,
  getWorksheetDownPayment,
} from '../../components/WorksheetLoanFactsPanel/WorksheetLoanFactsPanel.utils';

const KNOWN_PREFILL_SOURCES = new Set(Object.values(WORKSHEET_PREFILL_SOURCE));

const isFeePrefillSource = source => source.startsWith(WORKSHEET_FEE_PREFILL_SOURCE_PREFIX);

const getPrefillFeeNames = source =>
  source
    .slice(WORKSHEET_FEE_PREFILL_SOURCE_PREFIX.length)
    .split(WORKSHEET_FEE_PREFILL_NAMES_SEPARATOR)
    .map(_.trim)
    .filter(Boolean);

// Sources read off the loan's fees view (fetched only when a worksheet uses one).
const FEES_VIEW_PREFILL_SOURCES = new Set([
  WORKSHEET_PREFILL_SOURCE.LOAN_INITIAL_ESCROW_DEPOSIT,
  WORKSHEET_PREFILL_SOURCE.LOAN_MONTHLY_ESCROW,
  WORKSHEET_PREFILL_SOURCE.LOAN_PREPAID_FINANCE_CHARGES,
]);

export const isFeesViewPrefillSource = source =>
  isFeePrefillSource(source) || FEES_VIEW_PREFILL_SOURCES.has(source);

const LOAN_PREFILL_LABEL = {
  [WORKSHEET_PREFILL_SOURCE.LOAN_TOTAL_LOAN_AMOUNT]: 'Total Loan Amount',
  [WORKSHEET_PREFILL_SOURCE.LOAN_BASE_LOAN_AMOUNT]: 'Base Loan Amount',
  [WORKSHEET_PREFILL_SOURCE.LOAN_DOWN_PAYMENT]: 'Down Payment',
  [WORKSHEET_PREFILL_SOURCE.LOAN_INTEREST_RATE]: 'Interest Rate',
  [WORKSHEET_PREFILL_SOURCE.LOAN_TERM_MONTHS]: 'Term (months)',
  [WORKSHEET_PREFILL_SOURCE.LOAN_MONTHLY_PAYMENT]: 'Principal & Interest',
  [WORKSHEET_PREFILL_SOURCE.CHATTEL_SALES_PRICE]: 'Sales Price',
  [WORKSHEET_PREFILL_SOURCE.CHATTEL_SALES_TAX]: 'Sales Tax',
  [WORKSHEET_PREFILL_SOURCE.CHATTEL_MHIT]: 'MHIT',
  [WORKSHEET_PREFILL_SOURCE.CHATTEL_SECTION_COUNT]: 'number of home sections',
  [WORKSHEET_PREFILL_SOURCE.LOAN_INITIAL_ESCROW_DEPOSIT]: 'Initial Escrow Deposit',
  [WORKSHEET_PREFILL_SOURCE.LOAN_MONTHLY_ESCROW]: 'monthly escrow',
  [WORKSHEET_PREFILL_SOURCE.LOAN_PREPAID_FINANCE_CHARGES]: 'prepaid finance charges',
};

/**
 * The worksheet's top-level fields whose description opts them in to a known prefill source
 * (e.g. `prefill: loan.totalLoanAmount`), each with its source.
 */
export const getPrefillFields = fields =>
  fields
    .map(field => ({ ...field, source: parseWorksheetFieldSettings(field.description).prefill }))
    .filter(({ source }) => KNOWN_PREFILL_SOURCES.has(source) || isFeePrefillSource(source ?? ''));

const INVESTOR_PREFILL_SOURCE_PREFIX = 'investor.';

export const isInvestorPrefillSource = source => source.startsWith(INVESTOR_PREFILL_SOURCE_PREFIX);

export const isWorksheetAnswerEmpty = value => _.isNil(value) || value === '';

// Answers are compared as numbers when both read as one (a saved '80495.30' is the loan's '80495.3').
export const isSameWorksheetAnswer = (answer, value) => {
  const answerNumber = Number(answer);
  const valueNumber = Number(value);

  return _.isFinite(answerNumber) && _.isFinite(valueNumber)
    ? answerNumber === valueNumber
    : String(answer) === String(value);
};

const buildInvestorPrefillHint = ({ name, chattelMaxAdvancePercent }) =>
  `Default from investor ${name}: ${chattelMaxAdvancePercent}%`;

// LEN-194: the closing investor's Chattel Revenue Sheet terms (and its name).
const buildInvestorTermsPrefillValues = investor => ({
  [WORKSHEET_PREFILL_SOURCE.INVESTOR_NAME]: investor.fullName || investor.name,
  [WORKSHEET_PREFILL_SOURCE.INVESTOR_CHATTEL_RESERVE_PERCENT]: investor.chattelReservePercent,
  [WORKSHEET_PREFILL_SOURCE.INVESTOR_CHATTEL_SPREAD_PERCENT]: investor.chattelSpreadPercent,
  [WORKSHEET_PREFILL_SOURCE.INVESTOR_CHATTEL_RESERVE_RATE]: investor.chattelReserveRate,
  [WORKSHEET_PREFILL_SOURCE.INVESTOR_CHATTEL_INVESTOR_STATE]: investor.chattelInvestorState,
  // A date-only value the API sends as midnight UTC: read the calendar day, not the browser's local time.
  [WORKSHEET_PREFILL_SOURCE.INVESTOR_CHATTEL_AGREEMENT_DATE]: toWorksheetDateValue(
    parseCalendarDate(investor.chattelAgreementDate),
  ),
  [WORKSHEET_PREFILL_SOURCE.INVESTOR_CHATTEL_MAX_DELIVERY_AND_SET_PER_SIDE]:
    investor.chattelMaxDeliveryAndSetPerSide,
});

// Chattel details record up to three sections (serial #, HUD label, size).
const MAX_HOME_SECTIONS = 3;

const HOME_SECTION_NUMBERS = _.range(1, MAX_HOME_SECTIONS + 1);

const getSectionValues = (chattelDetails, sectionNumber) => {
  const suffix = sectionNumber > 1 ? sectionNumber : '';

  return [
    chattelDetails[`serialNumber${suffix}`],
    chattelDetails[`hudLabelNumber${suffix}`],
    chattelDetails[`width${sectionNumber}`],
    chattelDetails[`length${sectionNumber}`],
  ];
};

// LEN-194: sides of the home (single-wide 1, double-wide 2, ...) — the last of sections 1–3 that has
// a serial #, HUD label or size; null when the chattel details record none.
const getHomeSectionCount = chattelDetails => {
  if (!chattelDetails) {
    return null;
  }

  const filledSections = HOME_SECTION_NUMBERS.filter(sectionNumber =>
    getSectionValues(chattelDetails, sectionNumber).some(
      value => !_.isNil(value) && value !== '' && value !== 0,
    ),
  );

  return _.max(filledSections) ?? null;
};

const buildLoanPrefillValues = ({ loanInfo, subjectProperty, chattelDetails }) => {
  const { program, details } = loanInfo?.loan ?? {};

  return {
    [WORKSHEET_PREFILL_SOURCE.LOAN_TOTAL_LOAN_AMOUNT]: details?.totalLoanAmount,
    [WORKSHEET_PREFILL_SOURCE.LOAN_BASE_LOAN_AMOUNT]: details?.baseLoanAmount,
    [WORKSHEET_PREFILL_SOURCE.LOAN_DOWN_PAYMENT]: getWorksheetDownPayment({
      loanInfo,
      chattelDetails,
    }),
    [WORKSHEET_PREFILL_SOURCE.LOAN_INTEREST_RATE]: program?.rate,
    [WORKSHEET_PREFILL_SOURCE.LOAN_TERM_MONTHS]: program?.loanPeriod,
    [WORKSHEET_PREFILL_SOURCE.LOAN_MONTHLY_PAYMENT]:
      getProposedPropertyExpenses(subjectProperty)?.principalAndInterest,
    [WORKSHEET_PREFILL_SOURCE.CHATTEL_SALES_PRICE]: chattelDetails?.salesPrice,
    [WORKSHEET_PREFILL_SOURCE.CHATTEL_SALES_TAX]: chattelDetails?.salesTax,
    [WORKSHEET_PREFILL_SOURCE.CHATTEL_MHIT]: chattelDetails?.mhit,
    [WORKSHEET_PREFILL_SOURCE.CHATTEL_SECTION_COUNT]: getHomeSectionCount(chattelDetails),
  };
};

const getCollectedImpounds = ({ sectionG, taxesInsuranceEscrowed }) =>
  (sectionG ?? []).filter(impound => checkIsImpoundCollected(impound, taxesInsuranceEscrowed));

// The Fees page's Initial Escrow (section G) total: each collected escrow line, plus the aggregate
// adjustment when taxes and insurance are escrowed.
const getInitialEscrowDeposit = feesView =>
  _.sumBy(
    getCollectedImpounds(feesView),
    impound => (impound.monthsCollected ?? 0) * (impound.monthlyAmount ?? 0),
  ) + (feesView.taxesInsuranceEscrowed ? (feesView.aggregateAdjustment ?? 0) : 0);

// The escrow part of the monthly payment (CIS's revenue sheet: PITI 2,003.30 = P&I 1,732.92 +
// escrow 270.38): the monthly amount of each collected escrow line.
const getMonthlyEscrow = feesView =>
  _.sumBy(getCollectedImpounds(feesView), impound => impound.monthlyAmount ?? 0);

const getBorrowerPaidAmount = fee =>
  (fee.borrowerPaidBeforeClosingAmount ?? 0) + (fee.borrowerPaidAtClosingAmount ?? 0);

// LEN-194: the TILA prepaid finance charges (the Chattel Revenue Sheet's Prepaid Fees, so its Amount
// Financed matches the disclosed one): what the borrower pays of each fee counted toward the APR, the
// prepaids counted toward it, and the prepaid interest.
const getPrepaidFinanceCharges = ({ sections, sectionF }) =>
  _.sumBy(
    Object.values(sections ?? {})
      .flatMap(section => section?.fees ?? [])
      .filter(fee => fee?.countTowardsApr),
    getBorrowerPaidAmount,
  ) +
  _.sumBy(
    (sectionF?.prepaids ?? []).filter(prepaid => prepaid?.countTowardsApr),
    prepaid => prepaid.totalCollected ?? 0,
  ) +
  (sectionF?.prepaidInterestAmount ?? 0);

const FEES_VIEW_AMOUNT_GETTER = {
  [WORKSHEET_PREFILL_SOURCE.LOAN_INITIAL_ESCROW_DEPOSIT]: getInitialEscrowDeposit,
  [WORKSHEET_PREFILL_SOURCE.LOAN_MONTHLY_ESCROW]: getMonthlyEscrow,
  [WORKSHEET_PREFILL_SOURCE.LOAN_PREPAID_FINANCE_CHARGES]: getPrepaidFinanceCharges,
};

// Every fee of those names (case-insensitive) in sections A–H, plus the prepaids of those names; null
// when the loan has none.
const getFeeAmount = ({ sections, sectionF }, feeNames) => {
  const isNamed = fee => feeNames.includes(_.toLower(_.trim(fee?.feeName)));
  const fees = Object.values(sections ?? {})
    .flatMap(section => section?.fees ?? [])
    .filter(isNamed);
  const prepaids = (sectionF?.prepaids ?? []).filter(isNamed);

  return fees.length || prepaids.length
    ? _.round(
        _.sumBy(fees, fee => fee.amount ?? 0) + _.sumBy(prepaids, fee => fee.totalCollected ?? 0),
        2,
      )
    : null;
};

/**
 * The default answer and its hint for each fee prefill source in `sources` that the loan's fees view
 * answers (LEN-194: the Chattel Funding Worksheet's wire calculation).
 *
 * @returns {Object<string, { value: string, hint: string }>} by prefill source
 */
export const buildWorksheetFeesPrefillValues = ({ feesView, sources }) => {
  if (!feesView) {
    return {};
  }

  return _.omitBy(
    _.fromPairs(
      sources.filter(isFeesViewPrefillSource).map(source => {
        if (FEES_VIEW_PREFILL_SOURCES.has(source)) {
          return [
            source,
            {
              value: String(_.round(FEES_VIEW_AMOUNT_GETTER[source](feesView), 2)),
              hint: `From the loan's ${LOAN_PREFILL_LABEL[source]}`,
            },
          ];
        }

        const amount = getFeeAmount(feesView, getPrefillFeeNames(source));

        return [
          source,
          _.isNil(amount) ? null : { value: String(amount), hint: "From the loan's fees" },
        ];
      }),
    ),
    _.isNil,
  );
};

/**
 * The default answer and its hint for each prefill source the loan (getWorksheetLoanFactsAction's
 * raw responses) and the closing investor can answer. A source the loan leaves empty is omitted,
 * so its field stays blank for the user to type.
 *
 * @returns {Object<string, { value: string, hint: string }>} by prefill source
 */
export const buildWorksheetPrefillValues = ({ loanFacts, investor }) => {
  const loanValues = loanFacts
    ? _.mapValues(buildLoanPrefillValues(loanFacts), (value, source) =>
        _.isNil(value)
          ? null
          : { value: String(value), hint: `From the loan's ${LOAN_PREFILL_LABEL[source]}` },
      )
    : {};
  const investorPercent = investor?.chattelMaxAdvancePercent;
  const investorTermsValues = investor
    ? _.mapValues(buildInvestorTermsPrefillValues(investor), value =>
        _.isNil(value) || value === ''
          ? null
          : { value: String(value), hint: `Default from investor ${investor.name}` },
      )
    : {};

  return _.omitBy(
    {
      ...loanValues,
      ...investorTermsValues,
      [WORKSHEET_PREFILL_SOURCE.INVESTOR_CHATTEL_MAX_ADVANCE_PERCENT]: _.isNil(investorPercent)
        ? null
        : { value: String(investorPercent), hint: buildInvestorPrefillHint(investor) },
    },
    _.isNil,
  );
};
